import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {inject, Injectable} from "@angular/core";
import {AddWeightMeasurementRequest} from "src/app/infrastructure/moovement-api/web-api/animal.api-client";
import {catchError, firstValueFrom, from, lastValueFrom, Observable, of, switchMap, throwError} from "rxjs";
import {
  Animal,
  Asset,
  DeviceAssociation,
  FixedSamplePoint,
  LoRaGateway,
  Persistable,
  Pregnancy,
  SynchronizationStatus,
  WaterPoint,
} from "src/app/infrastructure/moovement-api/models/asset";
import {environment} from "src/app/environments/environment";
import {HttpErrorResponse} from "@angular/common/http";
import {ActionType, DataType} from "src/app/domain/domain-models";
import {NetworkStatusStore} from "src/app/infrastructure/network/network-status.store";
import {
  InternalNotificationsStore,
  InternalNotificationType
} from "src/app/infrastructure/internal-notifications/internal-notifications.store";
import {ScheduledRequest, ScheduledRequestType} from "src/app/infrastructure/scheduled-requests/scheduled-requests";
import {FarmStore} from "src/app/domain/assets/farm/farm.store";
import {randomUuid} from "src/app/domain/util/uuid/uuid";
import {GatewayService} from "src/app/domain/devices/gateway/gateway.service";
import {AnimalsService} from "src/app/domain/assets/animals/animals.service";
import {WaterPointsService} from "src/app/domain/assets/water-points/water-points.service";
import {FixedSamplePointsService} from "src/app/domain/assets/fixed-sample-points/fixed-sample-points.service";
import {DeviceAssociationStore} from "src/app/domain/devices/device-association.store";
import {ScheduledUpdate, ScheduledUpdates} from "src/app/infrastructure/scheduled-updates/scheduled-updates";
import {AssetSampleApiClient} from "src/app/infrastructure/moovement-api/web-api/asset-sample.api-client";
import {DeviceAssociationService} from "src/app/domain/devices/device-association.service";
import {DeadLetterReason} from "src/app/infrastructure/moovement-api/models/dead-letter";
import {DeviceStore} from "src/app/domain/devices/device.store";
import {AggregatedDeviceStateService} from "src/app/domain/devices/aggregated-state/aggregated-device-state.service";
import {FarmService} from "src/app/domain/assets/farm/farm.service";
import {Farm} from "src/app/infrastructure/moovement-api/models/farm";

const maxRetries = 3;

@Injectable({providedIn: 'root'})
export class DataSynchronisationService {

  private readonly isNativeEnvironment = environment.platform == 'native'
  private readonly actionMap: Map<DataType, Map<ActionType, (farmId: string, persistable: Persistable) => Observable<any>>> = new Map();

  private readonly deviceAssociationStore = inject(DeviceAssociationStore);
  private readonly internalNotificationsStore = inject(InternalNotificationsStore);
  private readonly deviceStore = inject(DeviceStore);
  private readonly aggregatedDeviceStateService = inject(AggregatedDeviceStateService);
  private readonly farmStore = inject(FarmStore);
  private readonly networkStatusStore = inject(NetworkStatusStore);
  private readonly farmService = inject(FarmService);

  constructor(
    private readonly animalsService: AnimalsService,
    private readonly assetSampleApiClient: AssetSampleApiClient,
    private readonly dataStorageService: DataStorageService,
    private readonly deviceAssociationService: DeviceAssociationService,
    private readonly fixedSamplePointsService: FixedSamplePointsService,
    private readonly gatewayService: GatewayService,
    private readonly waterPointsService: WaterPointsService
  ) {

    this.actionMap.set(DataType.farms, new Map([
      [ActionType.mergeAsset, (_, assetToSync) => this.farmService.updateFarm(assetToSync as Farm)]]))
    this.actionMap.set(DataType.animals, new Map([
      [ActionType.mergeAsset, (_, assetToSync) => this.animalsService.mergeAnimal(assetToSync as Animal)]]))
    this.actionMap.set(DataType.waterPoints, new Map([
      [ActionType.createAsset, (_, assetToSync) => this.waterPointsService.create(assetToSync as WaterPoint)],
      [ActionType.mergeAsset, (_, assetToSync) => this.waterPointsService.mergeWaterPoint(assetToSync as WaterPoint)]]))
    this.actionMap.set(DataType.loRaGateways, new Map([
      [ActionType.createAsset, (_, assetToSync) => this.gatewayService.create(assetToSync as LoRaGateway)],
      [ActionType.mergeAsset, (_, assetToSync) => this.gatewayService.merge(assetToSync as LoRaGateway)]]))
    this.actionMap.set(DataType.proximitySamplePoints, new Map([
      [ActionType.createAsset, (_, assetToSync) => this.fixedSamplePointsService.create(assetToSync as FixedSamplePoint)],
      [ActionType.mergeAsset, (_, assetToSync) => this.fixedSamplePointsService.update(assetToSync as FixedSamplePoint)]]));
    this.actionMap.set(DataType.precipitationSamplePoints, new Map([
      [ActionType.mergeAsset, (_, assetToSync) => this.fixedSamplePointsService.update(assetToSync as FixedSamplePoint)]]))
    this.actionMap.set(DataType.deviceAssociations, new Map([
      [ActionType.associateAssetWithDevice, (farmId, assetToSync) => this.deviceAssociationService.associateDevice(farmId, (assetToSync as DeviceAssociation).deviceId, (assetToSync as DeviceAssociation).assetId)],
      [ActionType.mergeAsset, (farmId, assetToSync) => this.deviceAssociationService.mergeAssociation(farmId, assetToSync as DeviceAssociation)],
    ]))
  }

  async synchronise() {
    const shouldSyncData = this.isNativeEnvironment && this.networkStatusStore.isOnline() && !this.farmStore.loading();

    if (!shouldSyncData) return;

    await this.synchronizeDissociatedAssets();
    await this.synchronizeDeletedGateways();
    await this.synchronizeAlteredAssets(DataType.animals, ActionType.mergeAsset);
    await this.synchronizeAlteredAssets(DataType.waterPoints, ActionType.mergeAsset);
    await this.synchronizeAlteredAssets(DataType.loRaGateways, ActionType.createAsset);
    await this.synchronizeAlteredAssets(DataType.loRaGateways, ActionType.mergeAsset);
    await this.synchronizeAlteredAssets(DataType.proximitySamplePoints, ActionType.createAsset);
    await this.synchronizeAlteredAssets(DataType.proximitySamplePoints, ActionType.mergeAsset);

    // Device associations may depend on the successful creation of assets earlier
    await this.synchronizeAlteredAssets(DataType.deviceAssociations, ActionType.mergeAsset);

    // Pull fresh state only after all pending local changes have been pushed,
    // so we don't overwrite not-yet-synced local edits with stale server data.
    this.synchroniseNewAssets();

    await this.performScheduledRequests();

    await this.performAllScheduledUpdates();
  }

  /**@deprecated in favor of scheduleUpdates*/
  async applyAction<P extends Persistable>(dataType: DataType, actionType: ActionType, asset: P, status: SynchronizationStatus): Promise<void> {
    const farm = this.farmStore.farm();
    if (!farm) throw new Error("No farm selected");

    const farmId = farm.id;

    if (this.isNativeEnvironment) {
      await this.dataStorageService.saveAsset(dataType, farmId, asset, status);
      await this.synchronizeAlteredAssets(dataType, actionType);
    } else {
      await this.syncActionToBackend(dataType, actionType, farmId, asset)
    }
  }

  private async performAllScheduledUpdates() {
    const pendingUpdates = await this.dataStorageService.getScheduledUpdates();
    for (const updates of pendingUpdates) {
      try {
        await this.performScheduledUpdates(updates);
      } catch (error) {
        console.error("Unexpected error while performing scheduled updates", error);
      }
    }
  }

  private async performScheduledUpdatesImmediately(updates: ScheduledUpdates) {
    for (const update of updates.updates) {
      try {
        await firstValueFrom(this.performScheduledUpdate(update));
        update.synchronized = true;
      } catch {
        if (updates.failureMessage) {
          this.internalNotificationsStore.addNotification(updates.failureMessage, InternalNotificationType.ERROR, true);
        }
        break;
      }
    }

    if (updates.updates.every(update => update.synchronized) && updates.successMessage) {
      this.internalNotificationsStore.addNotification(updates.successMessage, InternalNotificationType.SUCCESS, false);
    }
  }

  private async performScheduledUpdates(updates: ScheduledUpdates) {
    for (const update of updates.updates) {
      if (update.synchronized) continue;

      try {
        await firstValueFrom(this.performScheduledUpdate(update));
        update.synchronized = true;
        await this.dataStorageService.persistScheduledUpdates(updates);
      } catch (error) {
        if (error instanceof HttpErrorResponse && error.status === 409) {
          if (updates.failureMessage) {
            this.internalNotificationsStore.addNotification(updates.failureMessage, InternalNotificationType.ERROR, true);
          }
          //todo: add retry mechanism: pull changes, (automatically) solve merge conflicts, retry
          await this.sendToDeadLetter(updates, 'conflict');
        } else if (update.numberOfAttempts && update.numberOfAttempts >= maxRetries - 1) {
          if (updates.failureMessage) {
            this.internalNotificationsStore.addNotification(updates.failureMessage, InternalNotificationType.ERROR, true, undefined, `Response status: ${(error as any)?.status || 'None'}, exception: ${JSON.stringify(error)}`);
          }
          await this.sendToDeadLetter(updates, 'maxRetries');
        } else {
          if (updates.failureMessage) {
            this.internalNotificationsStore.addNotification(`${update.type} attempt ${update.numberOfAttempts} failed, retrying...`, InternalNotificationType.ERROR, true, undefined, `Response status: ${(error as any)?.status || 'None'}, exception: ${JSON.stringify(error)}`);
          }
          update.numberOfAttempts = (update.numberOfAttempts || 0) + 1;
          await this.dataStorageService.persistScheduledUpdates(updates);
        }
        break;
      }
    }

    if (updates.updates.every(update => update.synchronized)) {
      await this.dataStorageService.deleteScheduledUpdates(updates.id);
      if (updates.successMessage) {
        this.internalNotificationsStore.addNotification(updates.successMessage, InternalNotificationType.SUCCESS, false);
      }
    }
  }

  private performScheduledUpdate(update: ScheduledUpdate): Observable<any> {
    switch (update.type) {
      case "mergeAnimal": {
        return this.animalsService.mergeAnimal(update.data);
      }
      case "associateDevice": {
        const {farmId, deviceId, assetId} = update.data;
        return this.deviceAssociationService.associateDevice(farmId, deviceId, assetId);
      }
      case "recordNfcLocationSamples": {
        return this.assetSampleApiClient.recordNfcLocationSamples(update.data);
      }
      case "addWeightMeasurement": {
        const {animalId, weightMeasurement} = update.data;
        return this.animalsService.addWeightMeasurement(animalId, weightMeasurement);
      }
      case "setProfilePicture": {
        const {animalId, profilePictureUrl} = update.data;
        if (profilePictureUrl) {
          return from(fetch(profilePictureUrl).then(async response => response.blob())).pipe(
            switchMap(blob => this.animalsService.setProfilePicture(animalId, blob))
          );
        } else {
          return this.animalsService.deleteProfilePicture(animalId);
        }
      }
      default: {
        const message = `Unknown scheduled update '${update}'`;
        console.error(message);
        return throwError(() => new Error(message));
      }
    }
  }

  async scheduleUpdates(updates: ScheduledUpdates) {
    if (this.isNativeEnvironment) {
      if (updates.pendingAsset) {
        const farmId = this.farmStore.farm()?.id;
        if (farmId) {
          await this.dataStorageService.saveAsset(
            updates.pendingAsset.dataType,
            farmId,
            updates.pendingAsset.persistable,
            SynchronizationStatus.syncPending
          );
        }
      }
      await this.dataStorageService.persistScheduledUpdates(updates);
      if (this.networkStatusStore.isOnline()) {
        this.performAllScheduledUpdates();
      } else if (updates.scheduledMessage) {
        this.internalNotificationsStore.addNotification(updates.scheduledMessage, InternalNotificationType.INFO, false);
      }
    } else {
      this.performScheduledUpdatesImmediately(updates);
    }
  }

  private async sendToDeadLetter(updates: ScheduledUpdates, reason: DeadLetterReason): Promise<void> {
    await this.dataStorageService.saveDeadLetterEntry({
      id: randomUuid(),
      source: 'scheduledUpdates',
      json: JSON.stringify(updates),
      failedAt: new Date().toISOString(),
      reason
    });
    if (updates.pendingAsset) {
      await this.dataStorageService.deleteAsset(updates.pendingAsset.persistable.id);
    }
    await this.dataStorageService.deleteScheduledUpdates(updates.id);
  }


  async dissociateAsset(asset: Asset, dataType: DataType): Promise<void> {
    const farm = this.farmStore.farm();
    if (!farm) throw new Error("No farm selected");

    await this.dataStorageService.saveAsset(dataType, farm.id, asset, SynchronizationStatus.deleted);

    const associationsToRemove = this.deviceAssociationStore.assetIdToAssocations().get(asset.id);
    if (!associationsToRemove) return;

    for (const deviceAssociation of associationsToRemove) {
      const id = deviceAssociation.deviceId + deviceAssociation.assetId;
      await this.dataStorageService.saveAsset(DataType.deviceAssociations, farm.id, deviceAssociation!, SynchronizationStatus.deleted, id);
      await this.syncDissociationToBackend(deviceAssociation);
    }
  }

  async deleteGateway(gateway: LoRaGateway) {
    const farm = this.farmStore.farm();
    if (!farm) throw new Error("No farm selected");

    await this.dataStorageService.saveAsset(DataType.loRaGateways, farm.id, gateway, SynchronizationStatus.deleted);
    await this.syncGatewayDeletionToBackend(gateway);
  }

  async dissociateDeviceAssociation(deviceAssociation: DeviceAssociation): Promise<void> {
    const farm = this.farmStore.farm();
    if (!farm) throw new Error("No farm selected");

    await this.dataStorageService.saveAsset(DataType.deviceAssociations, farm.id, deviceAssociation!, SynchronizationStatus.deleted, deviceAssociation.deviceId! + deviceAssociation.assetId!);
    await this.syncDissociationToBackend(deviceAssociation);
  }

  private async synchronizeAlteredAssets(dataType: DataType, actionType: ActionType): Promise<void> {
    const farm = this.farmStore.farm();
    if (!farm) throw new Error("No farm selected");

    const farmId = farm.id;
    const assets = await this.dataStorageService.getAllAssetsMatching(dataType, farmId, this.toAssetState(actionType));
    for (const asset of assets) {
      await this.syncActionToBackend(dataType, actionType, farmId, asset);
    }
  }

  private synchroniseNewAssets() {
    const farm = this.farmStore.farm();
    if (farm) {
      const farmId = farm.id;
      // Loading fresh device associations here also refreshes animals, via
      // BootstrapService's effect on deviceAssociationStore.entities().
      this.deviceAssociationService.loadDeviceAssociations(farmId, this.deviceStore.entities());
      const deviceIds = this.deviceStore.entities().map(device => device.id);
      this.aggregatedDeviceStateService.getAggregatedDeviceStates(farmId, deviceIds);
    }
  }

  private toAssetState(actionType: ActionType): SynchronizationStatus {
    switch (actionType) {
      case ActionType.createAsset:
        return SynchronizationStatus.creationPending;
      case ActionType.mergeAsset:
        return SynchronizationStatus.syncPending;
      case ActionType.associateAssetWithDevice:
        return SynchronizationStatus.syncPending;
    }
  }

  private async syncActionToBackend(dataType: DataType, actionType: ActionType, farmId: string, assetToSync: Persistable): Promise<void> {
    const actionsForDataType = this.actionMap.get(dataType);
    if (!actionsForDataType) {
      console.error(`No actions defined for data type ${dataType}`);
      await this.handleUnrecoverableError(assetToSync);
      return;
    }

    const operation = actionsForDataType?.get(actionType);
    if (!operation) {
      console.error(`No operation defined for action type ${actionType} on data type ${dataType}`);
      this.handleUnrecoverableError(assetToSync);
      return;
    }

    const result$ = operation(farmId, assetToSync).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 409) {
          return from(this.handleConflict(dataType, assetToSync));
        }
        return from(this.handleRetryableError(dataType, actionType, farmId, assetToSync, error));
      })
    );

    return firstValueFrom(result$, {defaultValue: undefined});
  }

  private async handleConflict(dataType: DataType, assetToSync: Persistable) {
    const topic = this.toSingular(dataType);
    const name = assetToSync.name;
    const message = name === undefined
      ? `Changes to ${topic} could not be synced due to a version conflict and have been queued for manual processing.`
      : `Changes to '${name}' could not be synced due to a version conflict and have been queued for manual processing.`;

    this.internalNotificationsStore.addNotification(message, InternalNotificationType.WARNING, true);

    await this.dataStorageService.saveDeadLetterEntry({
      id: randomUuid(),
      source: 'applyAction',
      json: JSON.stringify({dataType, asset: assetToSync}),
      failedAt: new Date().toISOString(),
      reason: 'conflict'
    });
    await this.dataStorageService.deleteAsset(assetToSync.id);
  }

  private async handleUnrecoverableError(assetToSync: Persistable) {
    await this.dataStorageService.deleteAsset(assetToSync.id);
  }

  private async handleRetryableError(dataType: DataType, actionType: ActionType, farmId: string, assetToSync: Persistable, _error: any) {
    if (assetToSync.synchronizationAttempts && assetToSync.synchronizationAttempts >= maxRetries - 1) {
      const topic = this.toSingular(dataType);
      const name = assetToSync.name;
      const message = name === undefined
        ? `Changes to ${topic} could not be synced and have been queued for manual processing.`
        : `Changes to '${name}' could not be synced and have been queued for manual processing.`;
      this.internalNotificationsStore.addNotification(message, InternalNotificationType.ERROR, true);
      await this.dataStorageService.saveDeadLetterEntry({
        id: randomUuid(),
        source: 'applyAction',
        json: JSON.stringify({dataType, actionType, farmId, asset: assetToSync}),
        failedAt: new Date().toISOString(),
        reason: 'maxRetries'
      });
      await this.dataStorageService.deleteAsset(assetToSync.id);
    } else {
      assetToSync.synchronizationAttempts = (assetToSync.synchronizationAttempts || 0) + 1;
      await this.dataStorageService.saveAsset(dataType, farmId, assetToSync, assetToSync.synchronizationStatus!);
    }
  }

  private toSingular(dataType: DataType) {
    switch (dataType) {
      case DataType.animals:
        return "animal";
      case DataType.deviceAssociations:
        return "device assocation";
      case DataType.devices:
        return "device";
      case DataType.farms:
        return "farm";
      case DataType.fixedSamplePoints:
        return "fixed sample point";
      case DataType.loRaGateways:
        return "gateway";
      case DataType.paddocks:
        return "paddock";
      case DataType.precipitationSamplePoints:
        return "precipitation sample point";
      case DataType.proximitySamplePoints:
        return "proximity sample point";
      case DataType.waterPoints:
        return "water point";
    }
  }

  private async synchronizeDissociatedAssets() {
    const farm = this.farmStore.farm();
    if (!farm) return;

    const deletedAssociations = await this.dataStorageService.getAllAssetsMatching<DeviceAssociation>(DataType.deviceAssociations, farm.id, SynchronizationStatus.deleted);

    for (const deviceAssociation of deletedAssociations) {
      await this.syncDissociationToBackend(deviceAssociation);
    }
  }

  private syncDissociationToBackend(deviceAssociation: DeviceAssociation): Promise<void> {
    const compositeId = deviceAssociation.deviceId + deviceAssociation.assetId;
    return new Promise((resolve, reject) => {
      this.deviceAssociationService.dissociateDevice(deviceAssociation.deviceId).subscribe({
        next: async _ => {
          await this.dataStorageService.deleteAsset(compositeId);
          resolve();
        },
        error: async error => {
          if (error instanceof HttpErrorResponse) {
            const status = error.status;
            if (status === 400) {
              // in case the device association was never created to the server, we only need to remove the local references
              await this.dataStorageService.deleteAsset(compositeId);
              resolve();
              return;
            }
          }
          reject(error);
        }
      });
    });
  }

  private async synchronizeDeletedGateways() {
    const farm = this.farmStore.farm();
    if (!farm) return;

    const gateways = await this.dataStorageService.getAllAssetsMatching<LoRaGateway>(DataType.loRaGateways, farm.id, SynchronizationStatus.deleted);
    for (const gateway of gateways) {
      await this.syncGatewayDeletionToBackend(gateway);
    }
  }

  private syncGatewayDeletionToBackend(gateway: LoRaGateway) {
    const gateway$ = this.gatewayService.unassign(gateway);
    return lastValueFrom(gateway$);
  }

















  /**
   * @deprecated
   * This method can be removed once all previously scheduled requests have
   * been synchronized.
   *
   * No new requests can be scheduled since version 1.31.6. Once this version
   * (or a newer one) has been installed by all users, it is safe to remove
   * this method and all related code.
   */
  private async performScheduledRequests() {
    const pendingRequests = await this.dataStorageService.getScheduledRequests();
    for (const request of pendingRequests) {
      try {
        await firstValueFrom(this.performRequest(request));
        await this.dataStorageService.deleteScheduledRequest(request.id);
      } catch (error) {
        if (request.numberOfAttempts && request.numberOfAttempts >= maxRetries - 1) {
          console.error(`Unable to perform scheduled request after ${maxRetries} attempts: ${error}`);
          await this.dataStorageService.deleteScheduledRequest(request.id);
        } else {
          request.numberOfAttempts = (request.numberOfAttempts || 0) + 1;
          await this.dataStorageService.persistScheduledRequest(request);
        }
      }
    }
  }

  /**@deprecated*/
  private performRequest<D>(request: ScheduledRequest<D>): Observable<any> {
    switch (request.type) {
      case ScheduledRequestType.addWeightMeasurement: {
        const {animalId, weightMeasurement} = (request.data as {
          animalId: string,
          weightMeasurement: AddWeightMeasurementRequest
        });
        return this.animalsService.addWeightMeasurement(animalId, weightMeasurement);
      }
      case ScheduledRequestType.setProfilePicture: {
        const {animalId, profilePictureUrl} = (request.data as { animalId: string, profilePictureUrl?: string });
        if (profilePictureUrl) {
          return from(fetch(profilePictureUrl).then(async response => response.blob())).pipe(
            switchMap(blob => this.animalsService.setProfilePicture(animalId, blob))
          );
        } else {
          return this.animalsService.deleteProfilePicture(animalId);
        }
      }
      case ScheduledRequestType.setPregnancyStatus: {
        const {animalId, pregnancyStatus} = (request.data as {
          animalId: string,
          pregnancyStatus: Pregnancy
        });
        return this.animalsService.setPregnancyStatus(animalId, pregnancyStatus)
      }
      default:
        return of(undefined);
    }
  }

}
